import { MapProvider, useMap } from "@vis.gl/react-maplibre";
import { useEffect, useMemo, useRef } from "react";
import { getMapFitPadding, LIST_MAP_STYLE, SWEDEN_INITIAL_VIEW } from "../../lib/mapConfig";
import { regionSubtitle, REGIONS } from "../../lib/regions";
import type { Region, Spot } from "../../lib/types";
import { BaseMap } from "./BaseMap";
import { RegionMarker } from "./MapMarkers";

interface RegionsMapProps {
  spots: readonly Spot[];
  /** Called when the user taps a region marker — navigates to that
   *  region's spots overview. Zooming alone no longer triggers nav. */
  onRegionPick: (regionId: Region) => void;
}

/** Recent (`< RECENT_MS` ago) saved viewport restores on mount so a
 *  spots-map → regions-map back-transition feels continuous. Older entries
 *  are ignored so a fresh page load always shows the Sweden-wide default. */
const RECENT_MS = 5_000;

/**
 * Sweden-wide map with one count-disc marker per region. Tapping a
 * marker navigates to that region's spots overview; the map itself is
 * free for the user to zoom in/out without triggering a route change.
 */
export function RegionsMap({ spots, onRegionPick }: RegionsMapProps) {
  return (
    <MapProvider>
      <BaseMap
        mapStyle={LIST_MAP_STYLE}
        initialLongitude={SWEDEN_INITIAL_VIEW.longitude}
        initialLatitude={SWEDEN_INITIAL_VIEW.latitude}
        initialZoom={SWEDEN_INITIAL_VIEW.zoom}
        /* Wider than SWEDEN_BOUNDS so the regions overview can zoom out
           enough to keep all of Sweden visible on short or narrow
           containers. The default maxBounds (16.5° wide) clamps zoom
           too high at xl half-width columns. */
        maxBounds={[
          [0.0, 50.0],
          [35.0, 71.0],
        ]}
      >
        <RegionsLayer spots={spots} onRegionPick={onRegionPick} />
      </BaseMap>
    </MapProvider>
  );
}

function RegionsLayer({ spots, onRegionPick }: RegionsMapProps) {
  const { current: map } = useMap();
  const navigatedRef = useRef(false);

  const counts = useMemo(() => {
    const out: Record<Region, number> = {
      stockholm: 0,
      "east-sweden": 0,
      "south-sweden": 0,
      "west-sweden": 0,
      "central-sweden": 0,
      gotland: 0,
      oland: 0,
      "north-sweden": 0,
    };
    for (const s of spots) out[s.region]++;
    return out;
  }, [spots]);

  // On mount: if a recent viewport was saved (e.g. user just zoomed out
  // from a spots map), restore it so the transition feels continuous.
  // Otherwise fit Sweden's extent so the camera adapts to the container
  // size — shorter or narrower maps zoom out automatically. Padding
  // gives breathing room around the country outline and ensures the
  // North-Sweden marker disc isn't clipped.
  useEffect(() => {
    if (!map) return;
    // setPadding biases the camera persistently — needed because
    // fitBounds' padding option computes zoom but does not shift the
    // camera for asymmetric padding. On single-column the bottom
    // padding includes the drawer peek so the south of Sweden stays
    // visible above the drawer overhang. Apply this on both fresh
    // fits and stored-view restores: the stored center was captured
    // with this padding in effect, so jumpTo'ing it on a fresh map
    // (with no padding) would misplace the camera.
    map.setPadding(getMapFitPadding());
    const stored = readRecentView();
    if (stored) {
      map.jumpTo({ center: [stored.lng, stored.lat], zoom: stored.zoom });
      return;
    }
    // Defer fitBounds until the map has loaded so the camera fits the
    // actual container size rather than the pre-layout initial size.
    const applyDefault = () => {
      map.fitBounds(
        [
          [10.5, 55.0],
          [24.5, 69.5],
        ],
        { duration: 0 },
      );
    };
    if (map.loaded()) applyDefault();
    else map.once("load", applyDefault);
  }, [map]);

  // Persist viewport continuously so the spots-map can pick up where the
  // user left off when they tap a marker.
  useEffect(() => {
    if (!map) return;
    const persist = () => {
      const c = map.getCenter();
      sessionStorage.setItem(
        "listMapView",
        JSON.stringify({
          lng: c.lng,
          lat: c.lat,
          zoom: map.getZoom(),
          ts: Date.now(),
        }),
      );
    };
    map.on("moveend", persist);
    return () => {
      map.off("moveend", persist);
    };
  }, [map]);

  return (
    <>
      {REGIONS.map((region) => {
        const count = counts[region.id];
        if (count === 0) return null;
        return (
          <RegionMarker
            key={region.id}
            longitude={region.coords.lon}
            latitude={region.coords.lat}
            count={count}
            label={regionSubtitle(region) || region.label}
            onClick={() => {
              if (navigatedRef.current) return;
              navigatedRef.current = true;
              if (map) {
                const c = map.getCenter();
                sessionStorage.setItem(
                  "listMapView",
                  JSON.stringify({
                    lng: c.lng,
                    lat: c.lat,
                    zoom: map.getZoom(),
                    ts: Date.now(),
                  }),
                );
              }
              onRegionPick(region.id);
            }}
          />
        );
      })}
    </>
  );
}

function readRecentView(): { lng: number; lat: number; zoom: number } | null {
  try {
    const raw = sessionStorage.getItem("listMapView");
    if (!raw) return null;
    const v = JSON.parse(raw) as {
      lng: number;
      lat: number;
      zoom: number;
      ts?: number;
    };
    if (!v.ts || Date.now() - v.ts > RECENT_MS) return null;
    return { lng: v.lng, lat: v.lat, zoom: v.zoom };
  } catch {
    return null;
  }
}

